import {
  API_VERSION,
  CloneConversationType,
  IConversationMessage,
  NotificationType,
  useCloneConversation,
  useTranslation,
} from "@next-platform/client-common-core";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { IconVariants } from "@next-platform/client-common-spicy-ui";
import { ConversationSortOrder, GetConversationsResponse } from "@next-platform/packages-ts-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useApi } from "@/components";
import { MY_CHATS_CONVERSATION_LIMIT, WHITE_LABEL_API_VERSION } from "@/constants";
import { IChatsConversation, ILoadConversationsProps } from "@/types";

import useSharedChats from "./useSharedChats";
import useToast from "./useToast";

const savedChatsLogger = appLogger.withTag("useSavedChats");

interface IUseSavedChatsOptions {
  characterId: string;
}

export function useSavedChats({ characterId }: IUseSavedChatsOptions) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { displayToast } = useToast();

  const {
    onUserSelectedCloningType,
    clonedConversationInfo,
    isCloningConversationInProgress: isCloneConversationLoading,
    cloningConversationError,
    onResetCloneForNextUse,
    setLastMsgIndexForPartialCloning,
    onUserStartCloning,
  } = useCloneConversation();

  const [isCloneConversationModalVisible, setIsCloneConversationModalVisible] = useState(false);
  const [cloneConversationTitle, setCloneConversationTitle] = useState<string | null>(null);
  const refFetchId = useRef(0);

  const {
    filteredConversations,
    conversations,
    setConversations,
    selectedConversation,
    setSelectedConversation,
    isChangeTitleModalVisible,
    setIsChangeTitleModalVisible,
    isRemoveConversationModalVisible,
    setIsRemoveConversationModalVisible,
    onUserRemoveConversation,
    onUpdateConversationTitle,
    convertTime,
    isRemoveConversationLoading,
    isChangeTitleLoading,
    getCommonDropdownListItems,
    handleSearch,
    handleClearSearch,
    searchQuery,
    sortOrder,
    isSortRefetching,
    isLoadingMoreConversations,
    getActiveSortOrder,
    markSortApplied,
    setPageOneInFlight,
    isPageOneBlockingLoadMore,
    handleSortChange: handleSharedSortChange,
    runLoadMore,
    multiSelectProps,
  } = useSharedChats();
  const [lastConversationId, setLastConversationId] = useState<string | null>(null);

  const { jsonApiService, callApi: callApiForConversations, isLoading: isConversationsLoading } = useApi<GetConversationsResponse>();
  const isLoadMoreDisabled = isConversationsLoading || isSortRefetching || isLoadingMoreConversations;

  const getDropdownListItems = useCallback(
    (message: IChatsConversation) => {
      return [
        getCommonDropdownListItems(message)?.changeTitle,
        {
          label: t("common:cloneConversation"),
          onPress: () => {
            setSelectedConversation(message);
            setIsCloneConversationModalVisible(true);
            onUserStartCloning([{ id: message?.last_message_id, conversation_id: message?.id }] as unknown as IConversationMessage[]);
          },
          icon: IconVariants.COPY,
          isHidden: !message?.last_message_id,
        },
        getCommonDropdownListItems(message)?.removeConversation,
      ];
    },
    [
      setSelectedConversation,
      setIsChangeTitleModalVisible,
      setIsCloneConversationModalVisible,
      setIsRemoveConversationModalVisible,
      getCommonDropdownListItems,
      onUserStartCloning,
      t,
    ],
  );

  const onUserCloneConversation = (cloneType: CloneConversationType, cloneTitle: string) => {
    if (cloneType === CloneConversationType.PARTIAL) {
      setIsCloneConversationModalVisible(false);
      navigate(`/chat/${selectedConversation?.character_id}/${selectedConversation?.id}`, {
        state: {
          cloneType,
          cloneTitle: cloneTitle.trim(),
        },
      });
    } else {
      onUserSelectedCloningType(cloneType, cloneTitle);
      setCloneConversationTitle(cloneTitle);
    }
  };

  const loadConversations = useCallback(
    async ({ limit = MY_CHATS_CONVERSATION_LIMIT, lastId, sort }: ILoadConversationsProps = {}) => {
      const isPageOne = !lastId;
      if (!isPageOne && isPageOneBlockingLoadMore()) {
        return;
      }

      const fetchId = ++refFetchId.current;
      const activeSort = sort ?? getActiveSortOrder();
      if (isPageOne) {
        setPageOneInFlight(true);
      }
      const endpoint = `characters/${characterId}`;
      const subResource = "conversations";
      const params: Record<string, string | number> = { limit, sort: activeSort };
      if (lastId !== null && lastId !== "") {
        params.last_id = lastId as string;
      }

      try {
        const conversationsData = await callApiForConversations(
          jsonApiService.fetchResource,
          endpoint,
          subResource,
          params,
          WHITE_LABEL_API_VERSION as API_VERSION,
        );

        if (fetchId !== refFetchId.current) {
          return;
        }

        if (isPageOne) {
          markSortApplied(activeSort);
        }

        if (conversationsData) {
          setConversations((prev) => (lastId ? prev.concat(conversationsData as IChatsConversation[]) : (conversationsData as IChatsConversation[])));
        }

        if (conversationsData && conversationsData.length > 0) {
          const lastConversationCursorId = (conversationsData as IChatsConversation[]).find((conversation) => conversation?.is_last_id)?.id;
          setLastConversationId(lastConversationCursorId ?? null);
        } else {
          setLastConversationId(null);
        }
      } catch (error) {
        // Superseded requests must not reject — a stale failure would revert the sort dropdown.
        if (fetchId !== refFetchId.current) {
          return;
        }
        savedChatsLogger.error("Error when getting saved character's conversations:", error);
        throw error;
      } finally {
        if (isPageOne && fetchId === refFetchId.current) {
          setPageOneInFlight(false);
        }
      }
    },
    [
      callApiForConversations,
      characterId,
      getActiveSortOrder,
      isPageOneBlockingLoadMore,
      jsonApiService.fetchResource,
      markSortApplied,
      setConversations,
      setPageOneInFlight,
    ],
  );

  useEffect(() => {
    if (characterId) {
      setLastConversationId(null);
      void loadConversations().catch(() => {
        displayToast({ message: t("error:fallbackError"), type: NotificationType.error });
      });
    }
  }, [characterId, displayToast, loadConversations, t]);

  const handleSortChange = useCallback(
    (sort: ConversationSortOrder) => {
      handleSharedSortChange(sort, (nextSort) => loadConversations({ sort: nextSort }));
    },
    [handleSharedSortChange, loadConversations],
  );

  const insertClonedConversation = useCallback(
    (clonedConversation: IChatsConversation) => {
      setConversations((prev) => [clonedConversation, ...prev]);
    },
    [setConversations],
  );

  // Handle clone conversation result
  useEffect(() => {
    if (!clonedConversationInfo?.id && !cloningConversationError) {
      return;
    }

    if (clonedConversationInfo?.id) {
      displayToast({ message: t("conversation:toast.success.cloned"), type: NotificationType.success });
      if (selectedConversation) {
        insertClonedConversation({
          ...selectedConversation,
          ...clonedConversationInfo,
          label: cloneConversationTitle ?? "",
        });
      }
    }

    if (cloningConversationError) {
      displayToast({ message: t("error:fallbackError"), type: NotificationType.error });
    }

    setIsCloneConversationModalVisible(false);
    setSelectedConversation(null);
    setCloneConversationTitle(null);
    onResetCloneForNextUse();
    setLastMsgIndexForPartialCloning(-1);
  }, [
    clonedConversationInfo,
    cloningConversationError,
    selectedConversation,
    cloneConversationTitle,
    displayToast,
    insertClonedConversation,
    onResetCloneForNextUse,
    setLastMsgIndexForPartialCloning,
    setSelectedConversation,
    t,
  ]);

  const loadMoreConversations = useCallback(() => {
    void runLoadMore(() => loadConversations({ lastId: lastConversationId || undefined, sort: getActiveSortOrder() }));
  }, [getActiveSortOrder, lastConversationId, loadConversations, runLoadMore]);

  return {
    conversations,
    filteredConversations,
    isConversationsLoading,
    isLoadingMoreConversations,
    isLoadMoreDisabled,
    isSortRefetching,
    isRemoveConversationLoading,
    isChangeTitleLoading,
    isCloneConversationLoading,
    onUserRemoveConversation,
    convertTime,
    getDropdownListItems,
    selectedConversation,
    setSelectedConversation,
    isChangeTitleModalVisible,
    setIsChangeTitleModalVisible,
    isCloneConversationModalVisible,
    setIsCloneConversationModalVisible,
    isRemoveConversationModalVisible,
    setIsRemoveConversationModalVisible,
    onUpdateConversationTitle,
    onUserCloneConversation,
    onSearch: handleSearch,
    onClearSearch: handleClearSearch,
    loadMoreConversations,
    searchQuery,
    lastConversationId,
    sortOrder,
    handleSortChange,
    multiSelectProps,
  };
}

export default useSavedChats;
